import React, { useEffect, useState } from "react";
import api, { formatDate, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty } from "@/components/app/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, CalendarDays } from "lucide-react";

const empty = () => ({ name: "", holiday_date: "", holiday_type: "regular", note: "" });

export default function Holidays() {
    const [year, setYear] = useState(new Date().getFullYear());
    const [data, setData] = useState(null);
    const [editing, setEditing] = useState(null); // holiday form or null
    const [policy, setPolicy] = useState(null);

    const load = () => api.get(`/holidays?year=${year}`).then((r) => setData(r.data)).catch(() => {});
    useEffect(() => { load(); }, [year]);
    useEffect(() => { api.get("/holidays/policy").then((r) => setPolicy(r.data)).catch(() => {}); }, []);

    const togglePolicy = async () => {
        const next = !policy?.require_preapproval;
        try {
            const { data: d } = await api.put("/holidays/policy", { require_preapproval: next });
            setPolicy(d);
            toast.success(next
                ? "Holiday work now requires HR pre-approval before the premium is credited"
                : "Holiday work now counts immediately — clock-ins on holidays are auto-credited");
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const save = async () => {
        const f = editing;
        if (!f.name.trim() || !f.holiday_date) return toast.error("Name and date are required");
        try {
            if (f.id) await api.put(`/holidays/${f.id}`, f);
            else await api.post("/holidays", f);
            toast.success("Holiday saved — applies to all companies");
            setEditing(null); load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };
    const remove = async (h) => {
        if (!window.confirm(`Remove “${h.name}” (${h.holiday_date})?`)) return;
        try { await api.delete(`/holidays/${h.id}`); toast.success("Removed"); load(); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div>
            <PageHeader title="Holiday Management" subtitle="Working & non-working holidays for payroll. Applies to ALL companies automatically — payroll pays 200% (regular) / +30% (special) for work on these dates."
                actions={
                    <div className="flex items-center gap-2">
                        <select className="h-9 rounded-md border border-border bg-background px-2 text-sm" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                            {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                        <button onClick={() => setEditing(empty())} className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" data-testid="add-holiday-btn"><Plus className="size-4" /> Add Holiday</button>
                    </div>
                } />

            {data && (
                <div className="flex items-center gap-2 mb-3 text-xs">
                    <span className="badge badge-info">{data.counts.regular} regular</span>
                    <span className="badge badge-pending">{data.counts.special} special non-working</span>
                    <span className="text-muted-foreground">· Tenant-wide: every company inherits this calendar.</span>
                </div>
            )}

            {policy && (
                <Surface className="p-3 mb-3 flex items-center justify-between gap-3" data-testid="holiday-policy">
                    <div>
                        <div className="text-sm font-semibold">Holiday work requires pre-approval</div>
                        <div className="text-xs text-muted-foreground">
                            {policy.require_preapproval
                                ? "ON — holiday clock-ins land in Attendance Review as pending; the holiday premium is credited only after HR approves."
                                : "OFF — anyone who clocks in on a holiday is credited immediately; the premium flows to payroll automatically."}
                        </div>
                    </div>
                    <button onClick={togglePolicy} data-testid="holiday-policy-toggle"
                        className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${policy.require_preapproval ? "bg-primary" : "bg-muted-foreground/30"}`}>
                        <span className={`absolute top-0.5 size-5 rounded-full bg-white shadow transition-all ${policy.require_preapproval ? "left-6" : "left-0.5"}`} />
                    </button>
                </Surface>
            )}

            <TableWrap>
                <thead><tr><th>Date</th><th>Holiday</th><th>Type</th><th>Pay rule (if worked)</th><th>Note</th><th></th></tr></thead>
                <tbody>
                    {(data?.items || []).map((h) => (
                        <tr key={h.id}>
                            <td className="font-mono text-xs">{formatDate(h.holiday_date)}</td>
                            <td className="font-medium">{h.name}</td>
                            <td>{h.holiday_type === "regular" ? <span className="badge badge-info">regular</span> : <span className="badge badge-pending">special</span>}</td>
                            <td className="text-xs text-muted-foreground">{h.holiday_type === "regular" ? "200% of daily rate (100% even if unworked)" : "+30% premium"}</td>
                            <td className="text-xs">{h.note || "—"}</td>
                            <td>
                                <div className="flex items-center gap-2">
                                    <button className="text-xs text-primary" onClick={() => setEditing({ ...h })}>Edit</button>
                                    <button className="text-xs text-destructive" onClick={() => remove(h)}>Remove</button>
                                </div>
                            </td>
                        </tr>
                    ))}
                    {data && !data.items.length && <tr><td colSpan={6}><Empty title={`No holidays for ${year}`} subtitle="Add the year's proclaimed holidays." /></td></tr>}
                </tbody>
            </TableWrap>

            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editing?.id ? "Edit holiday" : "Add holiday"}</DialogTitle></DialogHeader>
                    {editing && (
                        <div className="space-y-2">
                            <div><Label>Name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Barangay Election Day" data-testid="holiday-name" /></div>
                            <div className="grid grid-cols-2 gap-2">
                                <div><Label>Date</Label><Input type="date" value={editing.holiday_date} onChange={(e) => setEditing({ ...editing, holiday_date: e.target.value })} data-testid="holiday-date" /></div>
                                <div><Label>Type</Label>
                                    <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={editing.holiday_type} onChange={(e) => setEditing({ ...editing, holiday_type: e.target.value })}>
                                        <option value="regular">Regular holiday (200%)</option>
                                        <option value="special">Special non-working (+30%)</option>
                                    </select></div>
                            </div>
                            <div><Label>Note</Label><Input value={editing.note || ""} onChange={(e) => setEditing({ ...editing, note: e.target.value })} placeholder="e.g. per Proclamation No. …" /></div>
                            <div className="text-xs text-muted-foreground flex items-center gap-1"><CalendarDays className="size-3" /> Applies to all companies; payroll premiums adjust automatically for attendance on this date.</div>
                        </div>
                    )}
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={save} data-testid="holiday-save">Save</button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
